import React, { useEffect } from 'react';
import { Trans } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { routes } from 'flow/types/routes';
import Page from './Page';
import Divider from 'flow/components/ui-components/Divider';
import useAppContext from 'flow/hooks/useAppContext';
import SettingSection from '../Setting/SettingSection';
import RouteLink from '../ui-components/RouteLink';
import SubHeader from './SubHeader';
import SettingRow from './SettingRow';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import { isValidSaveableNumber } from 'flow/utils/call-utils';
import { getUserCredential } from 'flow/actions/user-credential-action';
import { fetchExtension } from 'flow/actions/extension-actions';
import ResetPassword from 'flow/pages/settings/account/ResetPassword';
import { generatePath } from 'flow/utils/router-utils';
import { ExtensionKey } from 'flow/types/EntityKeys';

const Authentication = () => {
    const { i18next } = useAppContext();
    const history = useHistory();
    const dispatch = useAppDispatch();

    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();

    useEffect(() => {
        dispatch(getUserCredential(extensionKey));
        /*
        This is currently necessary to get the user's telephone numbers since they are not currently accessible
        through the UserCredentialResource. They may be in the future though, in which case this call can be removed.
        */
        dispatch(fetchExtension(extensionKey, 'statistsics'));
    }, [dispatch]);

    const credentials = useAppSelector((state) =>
        state.userCredentials
            .getIn([extensionKey, 'email'])
            ?.sort((a, b) =>
                a.get('credential') < b.get('credential') ? -1 : 1,
            ),
    );

    const usernames = useAppSelector((state) =>
        state.extensions.getIn([
            'extensions',
            extensionKey,
            'usage',
            'usernames',
        ]),
    );
    const phoneNumbers = usernames?.filter((username) =>
        isValidSaveableNumber(username),
    );

    const states = {
        verifying: 'settings.user.credential.not_verified',
        validated: 'settings.user.credential.current_email',
        onboarding_verifying: '',
        missing: '',
        legacy: '',
    };

    return (
        <Page
            header="settings.user.credential.username_password"
            onClickBack={() => {
                history.push(
                    generatePath(routes['settings.account'], { extensionKey }),
                );
            }}
        >
            <SettingSection>
                {i18next.t('settings.user.credential.usernames')}
            </SettingSection>

            <SubHeader>
                {i18next.t('settings.user.credential.email_address')}
            </SubHeader>

            {credentials ? (
                credentials.map((email) => (
                    <RouteLink
                        key={email.get('key')}
                        to={generatePath(
                            routes['settings.account.username-password.email'],
                            { extensionKey, credentialId: email.get('key') },
                        )}
                        description={i18next.t(states[email.get('state')])}
                    >
                        {email.get('credential')}
                    </RouteLink>
                ))
            ) : (
                <RouteLink
                    className="text-gray-500"
                    to={
                        routes[
                            'settings.account.username-password.email.register'
                        ]
                    }
                >
                    {i18next.t('settings.user.credential.no_email')}
                </RouteLink>
            )}

            <SubHeader>
                {i18next.t('settings.user.credential.phone_numbers')}
            </SubHeader>

            {phoneNumbers?.map((number) => (
                <SettingRow className="text-gray-800" key={number}>
                    {number}
                </SettingRow>
            ))}

            <Divider />

            <SettingSection>{i18next.t('signin.password')}</SettingSection>

            <ResetPassword extensionKey={extensionKey} />

            <Divider />

            <SettingSection>
                {i18next.t('settings.integrations.integration')}
            </SettingSection>

            <RouteLink
                to={routes['settings.account.username-password.manageTokens']}
            >
                {i18next.t('settings.integrations.manage_tokens')}
            </RouteLink>

            <div className="my-4 pl-2 text-xs text-gray-500">
                <Trans
                    i18n={i18next}
                    i18nKey="settings.integrations.use_integration_tokens"
                    components={[
                        <span key="0" className="font-bold text-black" />,
                    ]}
                />
            </div>
        </Page>
    );
};

export default Authentication;
